import {
  type Allocation,
  Box,
  Button,
  ButtonVariants,
  Checkbox,
  ConnectionType,
  Dropdown,
  EllipsisBoxWithTooltip,
  EMPTY_ARRAY,
  FormControlSizes,
  HStack,
  IconName,
  IndicatorBadge,
  LoaderSizes,
  LoaderTalos,
  type MultilegMarketDetails,
  ProductTypeEnum,
  type Security,
  type SideEnum,
  Stack,
  stringToOrderFormSide,
  Text,
  toBig,
  useCurrencyConversionRateValue,
  useDropdownPopper,
  type UseDropdownPopperProps,
  useDynamicCallback,
} from '@talos/kyoko';
import { compact } from 'lodash-es';
import { memo, type ReactNode, type RefObject, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { useIsMobileLayout } from '../../hooks';
import { useTradingSettings } from '../../providers/TradingSettingsContext';
import { usePanelContext } from '../Layout/providers/PanelContextProvider';
import { MarketAccountSelectionList } from '../MarketAccountSelectionList';
import { useMarketSelectorBuyingPowerEnrichment } from '../MarketSelector/enrichments/useMarketSelectorBuyingPowerEnrichment';
import type { MarketSelectorPanelProps } from '../MarketSelector/MarketSelectorPanel';
import { MarketSelectorPanelOnly } from '../MarketSelector/MarketSelectorPanelOnly';
import { useOrderMarketSelectorItems } from '../MarketSelector/Orders/useOrderMarketSelectorItems';
import { useMarketSelectorAggregationPresets } from '../MarketSelector/presets/useMarketSelectorAggregationPresets';
import type {
  MarketSelectorCustomPresetItem,
  MarketSelectorItem,
  MarketSelectorValue,
  UseMarketSelectorPanelParams,
} from '../MarketSelector/types';
import { useMarketSelector } from '../MarketSelector/useMarketSelector';
import { marketSelectorItemsToOldItems } from '../MarketSelector/utils/items';
import { useWrappedMarketSelectorValue } from '../MarketSelector/utils/useWrappedMarketSelectorValue';
import { OverflowBox } from './OverflowBox';
import { useValueAndState } from './useValueAndState';

export interface MultilegMarketSelectorLegButton {
  legIndex: number;
  displaySymbol: string;
  value: MarketSelectorValue;
  subText: string;
  subTextItems: string[];
}

export interface MultilegMarketSelectorProps {
  /** The index of the leg. Undefined means that it is the Unified Liquidity leg. */
  legIndex: number | undefined;
  isOpen: boolean;
  close: () => void;
  onChange: (accounts: string[]) => void;
  /** The leg's security */
  security: Security | undefined;
  /** An array of the selected accounts */
  value?: string[];
  /** Optional. Pass the items to select. If not provided, the selector will build its own items given the other params provided. */
  items?: MarketSelectorItem[] | null;
  /** Whether to render a standalone loader while selector items are being built. */
  showSpinner?: boolean;
  /**
   * Whether to show the multileg instrument "All Markets" toggle for regular legs.
   *
   * In multileg leg details, a nullish/omitted `Markets` value means the leg is valid on all markets.
   * This control exposes that instrument-level setting; it is not used for the synthetic UL leg.
   */
  showAllMarketsToggle?: boolean;
  /** The side of the leg (buy or sell). This will be relative to the order's side */
  side: SideEnum;
  /** The element ref to position the _new_ market selector relative to */
  marketSelectorAnchorRef: RefObject<HTMLElement>;
  /** The element ref to position the legacy dropdown relative to */
  dropdownAnchorRef: RefObject<HTMLElement>;
  /** Sub account allocations used to determine which markets accounts can be traded. */
  subAccountAllocations: Allocation[] | -1;
  legButtons?: MultilegMarketSelectorLegButton[];
  onLegClick?: (legIndex: number) => void;
  isMarketSelectorNarrow?: boolean;

  /** For showing Buying Power Exceeds By warnings, pass quantity and currency */
  orderQuantity?: string;
  /** For showing Buying Power Exceeds By warnings, pass quantity and currency */
  orderCurrency?: string;
  panelBottomSuffix?: MarketSelectorPanelProps['panelBottomSuffix'];
}

export const MultilegMarketSelector = memo(
  ({
    legIndex,
    isOpen,
    close,
    onChange,
    security,
    value = EMPTY_ARRAY,
    items: inputMarketSelectorItems,
    showSpinner = true,
    showAllMarketsToggle: inputShowAllMarketsToggle = false,
    side,
    marketSelectorAnchorRef,
    dropdownAnchorRef,
    subAccountAllocations,
    legButtons,
    onLegClick,
    isMarketSelectorNarrow,
    orderQuantity,
    orderCurrency,
    panelBottomSuffix,
  }: MultilegMarketSelectorProps) => {
    const { useNewMarketSelector } = useTradingSettings();

    const showAllMarketsToggle = useMemo(
      () => inputShowAllMarketsToggle && security?.ProductType === ProductTypeEnum.Spot,
      [security, inputShowAllMarketsToggle]
    );

    // The input value is just string[] so here we map / port it to a market selector value which can also wrap the selection with
    // the preset at least within the lifetime of this component for better UX. This'll be removed when the multileg account selection model is improved
    const { marketSelectorValue, onMarketSelectorValueChange } = useWrappedMarketSelectorValue(value);

    const handleNewMarketSelectorChange = useDynamicCallback((newValue: MarketSelectorValue) => {
      onChange(newValue.selections);
      onMarketSelectorValueChange(newValue);
    });

    const handleOldMarketSelectorChange = useDynamicCallback((selectedMarketAccounts: string[] = []) => {
      onChange(selectedMarketAccounts);
    });

    const buildOwnMarketSelectorItems = inputMarketSelectorItems === undefined;
    const ownMarketSelectorItems = useOrderMarketSelectorItems({
      /* A round-about way to do conditional computation in here... */
      security: buildOwnMarketSelectorItems ? security : undefined,
      subAccountAllocations,
      isUnifiedLiquidityEnabled: false,
    });

    const marketSelectorItems = buildOwnMarketSelectorItems ? ownMarketSelectorItems : inputMarketSelectorItems;
    const activeLegIndex = legIndex ?? -1;
    const panelTopPrefix =
      legButtons && onLegClick ? (
        <MultilegMarketSelectorLegButtons
          legButtons={legButtons}
          activeLegIndex={activeLegIndex}
          onLegClick={onLegClick}
          isNarrow={isMarketSelectorNarrow}
        />
      ) : undefined;

    if (!isOpen) {
      return null;
    }

    if (!marketSelectorItems) {
      return showSpinner ? <LoaderTalos size={LoaderSizes.XS} /> : null;
    }

    return useNewMarketSelector ? (
      <NewMultilegMarketSelector
        legIndex={legIndex}
        isOpen={isOpen}
        close={close}
        security={security}
        value={marketSelectorValue}
        marketSelectorItems={marketSelectorItems}
        showAllMarketsToggle={showAllMarketsToggle}
        onChange={handleNewMarketSelectorChange}
        anchorRef={marketSelectorAnchorRef}
        side={side}
        panelPlacement="left-end"
        panelTopPrefix={panelTopPrefix}
        panelContentKey={`${activeLegIndex}`}
        isMarketSelectorNarrow={isMarketSelectorNarrow}
        orderQuantity={orderQuantity}
        orderCurrency={orderCurrency}
        panelBottomSuffix={panelBottomSuffix}
      />
    ) : (
      <OldMultilegMarketSelector
        isOpen={isOpen}
        close={close}
        security={security}
        value={marketSelectorValue}
        marketSelectorItems={marketSelectorItems}
        showAllMarketsToggle={showAllMarketsToggle}
        onChange={handleOldMarketSelectorChange}
        anchorRef={dropdownAnchorRef}
      />
    );
  }
);

export const OldMultilegMarketSelector = ({
  isOpen,
  close,
  security,
  value,
  marketSelectorItems,
  showAllMarketsToggle,
  onChange,
  anchorRef,
  dropdownPlacement = 'top-end',
}: {
  isOpen: boolean;
  close: () => void;
  security: Security | undefined;
  value: MarketSelectorValue;
  marketSelectorItems: MarketSelectorItem[];
  showAllMarketsToggle: boolean;
  onChange: (newSelections: string[]) => void;
  anchorRef: RefObject<HTMLElement>;
  dropdownPlacement?: UseDropdownPopperProps['dropdownPlacement'];
}) => {
  const handleUseAllMarketsChanged = useDynamicCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    onChange(e.target.checked ? [] : value.selections);
  });

  const useAllMarkets = useMemo(() => value.selections.length === 0, [value]);

  const availableMarketAccounts = useMemo(
    () =>
      marketSelectorItemsToOldItems({ items: marketSelectorItems, includeInactiveMarketAccounts: false })
        .marketAccounts,
    [marketSelectorItems]
  );

  const handleClickOutside = useDynamicCallback(() => {
    close();
  });

  const dropdown = useDropdownPopper({
    isOpen,
    referenceElement: anchorRef.current,
    dropdownWidth: '325px',
    onClickOutside: handleClickOutside,
    dropdownPlacement,
  });

  if (!isOpen) {
    return null;
  }

  return (
    <Dropdown {...dropdown} portalize={true}>
      {showAllMarketsToggle ? (
        <Box
          fontWeight={600}
          background="colors.gray.030"
          borderColor="colors.gray.050"
          borderBottom={useAllMarkets ? undefined : 'solid 1px'}
          p="spacing8"
          data-testid="market-selector-all-markets-row"
          data-selected={useAllMarkets}
        >
          <Checkbox checked={useAllMarkets} onChange={handleUseAllMarketsChanged} disabled={false}>
            All Markets
          </Checkbox>
        </Box>
      ) : null}

      <Wrapper>
        <MarketAccountSelectionList
          availableMarketAccounts={availableMarketAccounts}
          selectedMarketAccounts={value.selections}
          security={security}
          onChangeMarketAccounts={onChange}
          connectionType={ConnectionType.Orders}
          showBuyingPower={false}
          showInactiveMarketAccounts={false}
        />
      </Wrapper>
    </Dropdown>
  );
};

const ALL_MARKETS_CUSTOM_PRESET_ITEM: MarketSelectorCustomPresetItem = {
  label: 'All Markets',
  id: '__all-markets__',
  onClick: api => api.unselectAll(),
  isSelected: api => api.selections.length === 0,
};

const CUSTOM_PRESET_ITEMS = [ALL_MARKETS_CUSTOM_PRESET_ITEM];

export const NewMultilegMarketSelector = ({
  legIndex,
  isOpen,
  close,
  value,
  marketSelectorItems,
  showAllMarketsToggle,
  onChange,
  security,

  panelPlacement = 'auto',
  anchorRef,
  side,
  panelTopPrefix,
  panelContentKey,
  isMarketSelectorNarrow,
  orderQuantity,
  orderCurrency,
  panelBottomSuffix,
}: {
  legIndex: number | undefined;
  isOpen: boolean;
  close: () => void;
  security: Security | undefined;
  value: MarketSelectorValue;
  marketSelectorItems: MarketSelectorItem[];
  showAllMarketsToggle: boolean;
  onChange: (newValue: MarketSelectorValue) => void;
  panelPlacement?: UseMarketSelectorPanelParams['panelPlacement'];
  anchorRef: RefObject<HTMLElement>;
  side: SideEnum;
  panelTopPrefix?: ReactNode;
  panelContentKey?: MarketSelectorPanelProps['panelContentKey'];
  isMarketSelectorNarrow?: boolean;
  orderQuantity?: string;
  orderCurrency?: string;
  panelBottomSuffix?: MarketSelectorPanelProps['panelBottomSuffix'];
}) => {
  const { isPopout } = usePanelContext();
  const isMobileLayout = useIsMobileLayout();

  const useConversionRate = security?.ProductType === ProductTypeEnum.Spot && security.BaseCurrency !== orderCurrency;

  const conversionRate = useCurrencyConversionRateValue(
    useConversionRate ? orderCurrency : undefined,
    useConversionRate ? security?.BaseCurrency : undefined
  );
  const quantityInBase = useMemo(() => {
    if (security?.BaseCurrency != null && security?.BaseCurrency === orderCurrency) {
      return orderQuantity;
    }

    const rateBig = toBig(conversionRate?.Rate);
    const quantityBig = toBig(orderQuantity);
    if (!rateBig || !quantityBig) {
      return undefined;
    }

    return rateBig.times(quantityBig).toFixed();
  }, [conversionRate?.Rate, orderQuantity, security?.BaseCurrency, orderCurrency]);

  const buyingPowerEnrichment = useMarketSelectorBuyingPowerEnrichment({
    symbol: security?.Symbol ?? '',
    currency: security?.BaseCurrency ?? '',
    threshold: '10s',
    quantity: quantityInBase,
    tag: 'NewOrderForm',
    side: stringToOrderFormSide(side),
  });

  const enrichmentSpecs = useMemo(() => compact([buyingPowerEnrichment]), [buyingPowerEnrichment]);

  const { aggregationPresets } = useMarketSelectorAggregationPresets();

  const marketSelectorAPI = useMarketSelector({
    value,
    onChange,
    presets: aggregationPresets,
    items: marketSelectorItems,
    enrichmentSpecs,
    customPresetItems: showAllMarketsToggle ? CUSTOM_PRESET_ITEMS : undefined,
  });

  // Tracks whether the component has been opened yet at all. Only then do we want to render the panel, since it can be heavy
  const [hasBeenOpened, setHasBeenOpened] = useState(false);
  useEffect(() => {
    setHasBeenOpened(curr => {
      return curr || isOpen;
    });
  }, [isOpen]);

  if (!hasBeenOpened) {
    return null;
  }

  return (
    <MarketSelectorPanelOnly
      marketSelectorAPI={marketSelectorAPI}
      isOpen={isOpen}
      onClose={close}
      panelOffset={0}
      panelAnchorRef={anchorRef}
      panelPlacement={panelPlacement}
      isNarrow={isPopout || isMobileLayout || isMarketSelectorNarrow}
      panelTopPrefix={panelTopPrefix}
      panelContentKey={panelContentKey}
      testID={`market-selector-panel-${legIndex ?? 'ul'}`}
      panelBottomSuffix={panelBottomSuffix}
      showBlotterOnOpen
    />
  );
};

const MultilegMarketSelectorLegButtons = ({
  legButtons,
  activeLegIndex,
  onLegClick,
  isNarrow,
}: {
  legButtons: MultilegMarketSelectorLegButton[];
  activeLegIndex: number;
  onLegClick: (legIndex: number) => void;
  isNarrow?: boolean;
}) => {
  return (
    <HStack
      gap={isNarrow ? 'spacing8' : 'spacing6'}
      p={isNarrow ? 'spacing16' : 'spacing8'}
      justifyContent="flex-start"
      overflow="auto"
    >
      {legButtons.map(leg => {
        const isActive = leg.legIndex === activeLegIndex;

        return (
          <Button
            key={getMultilegMarketSelectorLegKey(leg.legIndex)}
            size={FormControlSizes.Small}
            variant={isActive ? ButtonVariants.Priority : ButtonVariants.Default}
            onClick={() => onLegClick(leg.legIndex)}
            data-testid={`multileg-market-selector-leg-${getMultilegMarketSelectorLegKey(leg.legIndex)}`}
            height={40}
            width={isNarrow ? '100%' : undefined}
          >
            <HStack gap="spacing6" w="140px" justifyContent="space-between">
              <Stack alignItems="flex-start" overflow="hidden">
                <Text color="colorTextImportant">{leg.displaySymbol}</Text>
                <EllipsisBoxWithTooltip
                  color="colorTextDefault"
                  tooltip={
                    <Stack>
                      {leg.subTextItems.map((displayName, i) => (
                        <Text key={`${i}-${displayName}`}>{displayName}</Text>
                      ))}
                    </Stack>
                  }
                >
                  {leg.subText}
                </EllipsisBoxWithTooltip>
              </Stack>

              <IndicatorBadge>{leg.legIndex === -1 ? 'UL' : `Leg ${leg.legIndex + 1}`}</IndicatorBadge>
            </HStack>
          </Button>
        );
      })}
    </HStack>
  );
};

const getMultilegMarketSelectorLegKey = (legIndex: number) => (legIndex === -1 ? 'ul' : legIndex);

export const MultilegMarketAccountSelectorButton = ({
  selectedMarketAccounts,
  showChanges,
  marketDetails,
  readOnly,
  anchorRef,
  onClick,
  showAllMarketsToggle,
}: {
  selectedMarketAccounts: string[];
  showChanges?: boolean;
  marketDetails?: MultilegMarketDetails[];
  readOnly?: boolean;
  anchorRef?: RefObject<HTMLButtonElement>;
  onClick: () => void;
  showAllMarketsToggle?: boolean;
}) => {
  // This hook compares the multileg market details, compares to the current selection, and highlights the diff (additions, removals, etc)
  const items = useValueAndState(marketDetails, selectedMarketAccounts, showChanges);

  return (
    <Button
      endIcon={readOnly ? undefined : IconName.PencilField}
      size={FormControlSizes.Small}
      ref={anchorRef}
      onClick={onClick}
      justifyContent="space-between"
      data-testid="ml-market-edit-button"
      width="100%"
      disabled={readOnly}
    >
      <OverflowBox items={items} showAllMarketsToggle={showAllMarketsToggle} />
    </Button>
  );
};

const Wrapper = styled(Box)`
  scrollbar-gutter: stable both-edges;
`;
Wrapper.defaultProps = {
  maxHeight: '400px',
  overflow: 'auto',
  py: 'spacing8',
};
